import React, {useRef, useState, useCallback} from 'react';
import Styled from 'styled-components';
import {FormattedMessage, useIntl} from 'react-intl';
import {Modal, Icon, Text, FlexRow} from '@forecast-it/design-system';
import type {AttachedFile} from '../ai-project-planning/types';
import {
	NOVA_PURPLE,
	NOVA_PURPLE_DARK,
	NOVA_PURPLE_GRADIENT_2,
	NOVA_PURPLE_GRADIENT_3,
	NOVA_PURPLE_GRADIENT_4,
	BG_WHITE,
	BG_LIGHT,
	BG_DROPZONE,
	BG_CHOICE_CARD,
	BG_CHOICE_CARD_HIGHLIGHT_END,
	BG_CHOICE_CARD_NONHIGHLIGHT_START,
	BG_CHOICE_CARD_NONHIGHLIGHT_END,
	BG_ICON_CIRCLE_END,
	BORDER_INPUT,
	BORDER_HOVER,
	BORDER_SEPARATOR,
	BORDER_DROPZONE,
	BORDER_DROPZONE_ICON,
	BORDER_DIVIDER,
	DOT_AMBER,
	TEXT_MAIN,
	TEXT_MED,
	TEXT_FAINT,
	TEXT_GENERIC,
	TEXT_AUX,
	ERROR_COLOR_2,
} from '../ai-project-planning/colors';

// Pre-TNSR-635 modal, restored for the project_create_modal_redesign flag's OFF path (see
// generic_modal_conductor.js). Product retired this component when AiProjectPlanningModal became
// the sole Create Project entry point (commit 02b370f5f43: "did not match Figma... made state 1
// unreachable"), and shared styles.ts dropped several of this component's styled-components in the
// same redesign. Rather than reach back into styles.ts (whose current shapes serve the new modal's
// different layout), every styled-component below is defined locally with its original,
// pre-retirement CSS, so the legacy flag-off path renders exactly as it did in production before
// this PR shipped, independent of any later styling changes made for the new modal.

interface ProjectCreationChoiceModalProps {
	closeModal: () => void;
	onManual: () => void;
	onAI?: () => void; // kept — unused when aiEnabled=true
	onSow?: (file: AttachedFile) => void;
	aiEnabled?: boolean;
	/**
	 * The gen AI kill switch. The SOW drop zone stays on screen, greyed and inert, so the user can
	 * see the capability exists and has been switched off rather than finding the modal silently
	 * short of an option. Starting from scratch is unaffected.
	 */
	aiDisabled?: boolean;
}

const TitleDot = Styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${NOVA_PURPLE} 0%, ${NOVA_PURPLE_GRADIENT_4} 100%);
  flex-shrink: 0;
`;

const AccentBanner = Styled.div`
  background: linear-gradient(135deg, rgba(110, 15, 234, 0.04) 0%, rgba(57, 38, 100, 0.02) 100%);
  border-bottom: 1px solid rgba(110, 15, 234, 0.08);
  padding: 12px 32px;
`;

const Body = Styled.div`
  padding: 32px;
  display: flex;
  gap: 20px;
`;

const ChoiceCard = Styled.button<{highlight?: boolean}>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  padding: 24px;
  border-radius: 12px;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;
  border: 1.5px solid ${({highlight}) => (highlight ? NOVA_PURPLE : BORDER_INPUT)};
  background: ${({highlight}) =>
		highlight
			? 'linear-gradient(135deg, rgba(110,15,234,0.1) 0%, rgba(110,15,234,0.05) 100%)'
			: `linear-gradient(135deg, ${BG_CHOICE_CARD} 0%, ${BG_CHOICE_CARD_NONHIGHLIGHT_START} 100%)`};

  &:hover {
    border-color: ${({highlight}) => (highlight ? NOVA_PURPLE_DARK : BORDER_HOVER)};
    background: ${({highlight}) =>
		highlight
			? 'linear-gradient(135deg, rgba(110,15,234,0.15) 0%, rgba(110,15,234,0.08) 100%)'
			: `linear-gradient(135deg, ${BG_CHOICE_CARD_NONHIGHLIGHT_START} 0%, ${BG_CHOICE_CARD_NONHIGHLIGHT_END} 100%)`};
    transform: translateY(-2px);
    box-shadow: ${({highlight}) => (highlight ? '0 8px 24px rgba(110, 15, 234, 0.15)' : '0 8px 24px rgba(0,0,0,0.06)')};
  }

  &:active {
    transform: translateY(0);
  }
`;

const IconCircle = Styled.div<{highlight?: boolean}>`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({highlight}) =>
		highlight
			? `linear-gradient(135deg, ${NOVA_PURPLE} 0%, ${NOVA_PURPLE_GRADIENT_2} 100%)`
			: `linear-gradient(135deg, ${BG_CHOICE_CARD_HIGHLIGHT_END} 0%, ${BG_ICON_CIRCLE_END} 100%)`};
  flex-shrink: 0;
  color: ${({highlight}) => (highlight ? BG_WHITE : NOVA_PURPLE)};
  box-shadow: ${({highlight}) => (highlight ? '0 4px 16px rgba(110, 15, 234, 0.25)' : '0 2px 8px rgba(110, 15, 234, 0.08)')};
  transition: all 0.2s ease;
`;

const ChoiceTitle = Styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${TEXT_MAIN};
  letter-spacing: -0.3px;
`;

const ChoiceBadge = Styled.span`
  display: inline-block;
  background: linear-gradient(90deg, ${NOVA_PURPLE} 0%, ${NOVA_PURPLE_GRADIENT_3} 100%);
  color: ${BG_WHITE};
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  padding: 3px 10px;
  border-radius: 12px;
  margin-left: 8px;
  vertical-align: middle;
  box-shadow: 0 2px 8px rgba(110, 15, 234, 0.3);
`;

const ChoiceDesc = Styled.div`
  font-size: 13px;
  color: ${TEXT_GENERIC};
  line-height: 1.6;
  font-weight: 500;
`;

const Divider = Styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  flex-shrink: 0;
  color: ${TEXT_FAINT};
  font-size: 12px;
  font-weight: 600;
  padding: 0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    width: 1px;
    height: 100%;
    background: linear-gradient(180deg, transparent 0%, ${BORDER_SEPARATOR} 50%, transparent 100%);
  }
`;

const EntryBody = Styled.div`
    display: flex;
    align-items: stretch;
    gap: 16px;
    padding: 24px;
`;

const DropZoneCard = Styled.div<{isDragging?: boolean; isDisabled?: boolean}>`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 32px 24px;
    border-radius: 12px;
    border: 2px dashed ${({isDragging}) => (isDragging ? NOVA_PURPLE : BORDER_DROPZONE)};
    background: ${({isDragging}) =>
		isDragging
			? 'rgba(110,15,234,0.06)'
			: `repeating-linear-gradient(
                -45deg,
                transparent,
                transparent 8px,
                rgba(173, 205, 245, 0.35) 8px,
                rgba(173, 205, 245, 0.35) 9px
              ), ${BG_DROPZONE}`};
    cursor: ${({isDisabled}) => (isDisabled ? 'not-allowed' : 'pointer')};
    transition: border-color 0.15s;
    text-align: center;
    &:hover { border-color: ${NOVA_PURPLE}; }
`;

const DropZoneIconBox = Styled.div`
    width: 48px;
    height: 48px;
    border-radius: 10px;
    background: ${BG_WHITE};
    border: 1.5px solid ${BORDER_DROPZONE_ICON};
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
`;

const DropZoneIconDot = Styled.div`
    position: absolute;
    top: -3px;
    right: -3px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${DOT_AMBER};
    border: 2px solid ${BG_WHITE};
`;

const DropZoneTitle = Styled.div`
    font-size: 14px; font-weight: 600; color: ${TEXT_MAIN};
`;
const DropZoneBrowse = Styled.span`
    text-decoration: underline; color: ${NOVA_PURPLE}; cursor: pointer; font-weight: 600;
`;
const DropZoneSub = Styled.div`
    font-size: 13px; color: ${TEXT_MED}; line-height: 1.5;
`;
const FormatBadges = Styled.div`
    display: flex; gap: 6px; flex-wrap: wrap; justify-content: center;
`;
const FormatBadge = Styled.span`
    font-size: 11px; font-weight: 600; color: ${NOVA_PURPLE};
    background: rgba(110,15,234,0.08); border-radius: 4px; padding: 2px 7px;
`;

const ManualCard = Styled.button`
    flex: 1;
    display: flex; flex-direction: column; align-items: flex-start; gap: 10px;
    padding: 24px; border-radius: 12px; border: 1.5px solid ${BORDER_INPUT};
    background: ${BG_LIGHT}; cursor: pointer; text-align: left;
    outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;
    &:hover { border-color: ${BORDER_HOVER}; box-shadow: 0 4px 12px rgba(0,0,0,0.06); }
`;
const ManualCardTitle = Styled.div`
    font-size: 14px; font-weight: 700; color: ${TEXT_MAIN};
`;
const ManualCardDesc = Styled.div`
    font-size: 12px; color: ${TEXT_MED}; line-height: 1.5;
`;
const ManualCardTime = Styled.div`
    display: flex; align-items: center; gap: 4px;
    font-size: 11px; color: ${TEXT_AUX}; margin-top: 4px;
`;
const EntryPrivacyFooter = Styled.div`
    padding: 12px 24px; font-size: 12px; color: ${TEXT_AUX};
    border-top: 1px solid ${BORDER_DIVIDER};
`;

const ProjectCreationChoiceModal: React.FC<ProjectCreationChoiceModalProps> = ({
	closeModal,
	onManual,
	onAI,
	onSow,
	aiEnabled = false,
	aiDisabled = false,
}) => {
	const {formatMessage} = useIntl();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [fileError, setFileError] = useState<string | null>(null);

	const handleFile = useCallback(
		(file: File) => {
			setFileError(null);
			const ext = '.' + file.name.split('.').pop()?.toLowerCase();
			if (!['.pdf', '.docx', '.md'].includes(ext)) {
				setFileError(formatMessage({id: 'sow_upload.unsupported_format'}));
				return;
			}
			if (file.size > 20 * 1024 * 1024) {
				setFileError(formatMessage({id: 'sow_upload.file_too_large'}));
				return;
			}
			const reader = new FileReader();
			reader.onload = evt => {
				const b64 = (evt.target?.result as string).split(',')[1] ?? '';
				onSow?.({filename: file.name, mimeType: file.type, dataBase64: b64});
			};
			reader.onerror = () => {
				setFileError(formatMessage({id: 'sow_upload.file_read_error'}));
				// Reset so the user can retry with the same filename — the browser suppresses
				// onChange when the input value hasn't changed since the last pick.
				if (fileInputRef.current) fileInputRef.current.value = '';
			};
			reader.readAsDataURL(file);
		},
		[onSow, formatMessage]
	);

	const handleDrop = useCallback(
		(e: React.DragEvent<HTMLDivElement>) => {
			e.preventDefault();
			setIsDragging(false);
			const file = e.dataTransfer.files?.[0];
			if (file) handleFile(file);
		},
		[handleFile]
	);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) handleFile(file);
	};

	if (aiEnabled) {
		return (
			<Modal onCloseModal={closeModal} closeOnInteractOutside={false}>
				<Modal.Title>
					<FormattedMessage id="project_creation_choice_modal.title" />
				</Modal.Title>
				<Modal.Content style={{padding: 0}}>
					<div style={{padding: '12px 24px 0', fontSize: 13, color: TEXT_MED}}>
						<FormattedMessage id="project_creation_choice_modal.sow_subtitle" />
					</div>
					<EntryBody>
						<DropZoneCard
							isDragging={!aiDisabled && isDragging}
							isDisabled={aiDisabled}
							aria-disabled={aiDisabled}
							onDragOver={e => {
								e.preventDefault();
								if (!aiDisabled) setIsDragging(true);
							}}
							onDragLeave={() => setIsDragging(false)}
							onDrop={e => {
								// Swallowed rather than ignored: without preventDefault the browser
								// navigates away to the dropped file.
								e.preventDefault();
								if (!aiDisabled) handleDrop(e);
							}}
							onClick={() => {
								if (!aiDisabled) fileInputRef.current?.click();
							}}
							data-cy="sow-drop-zone"
						>
							<DropZoneIconBox>
								<Icon icon="upload" size="m" color={NOVA_PURPLE} />
								<DropZoneIconDot />
							</DropZoneIconBox>
							<DropZoneTitle>
								<FormattedMessage id="sow_upload.drop_here" />
							</DropZoneTitle>
							<DropZoneSub>
								<FormattedMessage id="sow_upload.or" />{' '}
								<DropZoneBrowse style={aiDisabled ? {cursor: 'not-allowed'} : undefined}>
									<FormattedMessage id="sow_upload.browse_files" />
								</DropZoneBrowse>
							</DropZoneSub>
							<DropZoneSub>
								<FormattedMessage id="sow_upload.nova_will_fill" />
							</DropZoneSub>
							<FormatBadges>
								<FormatBadge>PDF</FormatBadge>
								<FormatBadge>DOCX</FormatBadge>
								<FormatBadge>MD</FormatBadge>
							</FormatBadges>
							{aiDisabled && (
								<DropZoneSub style={{color: ERROR_COLOR_2}}>
									<FormattedMessage id="gen_ai.disabled_label" />
								</DropZoneSub>
							)}
							{fileError && <DropZoneSub style={{color: ERROR_COLOR_2}}>{fileError}</DropZoneSub>}
						</DropZoneCard>

						<ManualCard onClick={onManual} data-cy="project-creation-choice-scratch">
							<Icon icon="edit" size="m" />
							<div>
								<ManualCardTitle>
									<FormattedMessage id="sow_upload.manual_title" />
								</ManualCardTitle>
								<ManualCardDesc>
									<FormattedMessage id="sow_upload.manual_desc" />
								</ManualCardDesc>
								<ManualCardTime>
									<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
										<circle cx="6" cy="6" r="5" stroke={TEXT_AUX} strokeWidth="1.2" />
										<path
											d="M6 3.5V6l1.5 1.5"
											stroke={TEXT_AUX}
											strokeWidth="1.2"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
									</svg>
									<FormattedMessage id="sow_upload.manual_time" />
								</ManualCardTime>
							</div>
						</ManualCard>
					</EntryBody>
					<EntryPrivacyFooter>
						<FormattedMessage id="sow_upload.privacy_note" />
					</EntryPrivacyFooter>
					<input
						ref={fileInputRef}
						type="file"
						accept=".pdf,.docx,.md"
						style={{display: 'none'}}
						disabled={aiDisabled}
						onChange={handleInputChange}
						data-cy="sow-file-input"
					/>
				</Modal.Content>
				<Modal.Footer>
					<Modal.TertiaryFooterButton onPress={closeModal}>
						<FormattedMessage id="common.cancel" />
					</Modal.TertiaryFooterButton>
				</Modal.Footer>
			</Modal>
		);
	}

	return (
		<Modal onCloseModal={closeModal} closeOnInteractOutside={false}>
			<Modal.Title>
				<FlexRow gap="xs" alignItems="center">
					<TitleDot />
					<FormattedMessage id="project_creation_choice_modal.title" />
				</FlexRow>
			</Modal.Title>
			<Modal.Content style={{padding: '0'}}>
				<AccentBanner>
					<Text color="secondary" size={'2'}>
						<FormattedMessage id="project_creation_choice_modal.subtitle" />
					</Text>
				</AccentBanner>
				<Body>
					<ChoiceCard data-cy="project-creation-choice-scratch" onClick={onManual}>
						<IconCircle>
							<Icon icon="project" size="m" />
						</IconCircle>
						<div>
							<ChoiceTitle>
								<FormattedMessage id="project_creation_choice_modal.scratch_title" />
							</ChoiceTitle>
							<ChoiceDesc>
								<FormattedMessage id="project_creation_choice_modal.scratch_desc" />
							</ChoiceDesc>
						</div>
					</ChoiceCard>

					{aiEnabled && (
						<>
							<Divider>
								<FormattedMessage id="project_creation_choice_modal.divider" />
							</Divider>

							<ChoiceCard highlight onClick={onAI}>
								<IconCircle highlight>
									<Icon icon="sparkle" size="m" color="currentColor" />
								</IconCircle>
								<div>
									<ChoiceTitle>
										<FormattedMessage id="project_creation_choice_modal.ai_title" />
										<ChoiceBadge>
											<FormattedMessage id="project_creation_choice_modal.ai_badge" />
										</ChoiceBadge>
									</ChoiceTitle>
									<ChoiceDesc>
										<FormattedMessage id="project_creation_choice_modal.ai_desc" />
									</ChoiceDesc>
								</div>
							</ChoiceCard>
						</>
					)}
				</Body>
			</Modal.Content>
		</Modal>
	);
};

export default ProjectCreationChoiceModal;
